import { z } from 'zod';
import type { Result } from './result';
import { toContractError, type ContractError } from './errors';
import { QueueInfoSchema } from './http';
import { QueueReminderStateSchema } from './push';
import { UuidSchema } from './uuid';

const QueueAuthSchema = z
  .object({
    type: z.literal('auth'),
    status: z.union([z.literal('ok'), z.literal('error')]).optional(),
  })
  .passthrough();

const QueueHeartbeatSchema = z.object({
  type: z.literal('are-you-there'),
});

const QueueUpdateSchema = z.object({
  type: z.literal('queue-update'),
  queue: QueueInfoSchema,
});

const QueueAssignedSchema = z.object({
  type: z.literal('queue-assigned'),
  ticket_id: UuidSchema.optional(),
  session_id: UuidSchema.optional(),
  robot_id: UuidSchema,
  room_id: z.string().optional(),
  lease_token: z.string().optional(),
  lease_expires_at: z.string().optional(),
});

const QueueOfferedSchema = z.object({
  type: z.literal('queue-offered'),
  ticket_id: UuidSchema.optional(),
  session_id: UuidSchema.optional(),
  robot_id: UuidSchema,
  room_id: z.string().optional(),
  offer_expires_at: z.string().optional(),
});

const QueueStatusAssignedSchema = z.object({
  status: z.literal('assigned'),
  ticket_id: UuidSchema.optional(),
  session_id: UuidSchema.optional(),
  robot_id: UuidSchema,
  room_id: z.string().optional(),
  lease_token: z.string().optional(),
  lease_expires_at: z.string().optional(),
  reminder: QueueReminderStateSchema.optional(),
});

const QueueStatusOfferedSchema = z.object({
  status: z.literal('offered'),
  ticket_id: UuidSchema.optional(),
  session_id: UuidSchema.optional(),
  robot_id: UuidSchema,
  room_id: z.string().optional(),
  offer_expires_at: z.string().optional(),
  reminder: QueueReminderStateSchema.optional(),
});

const QueueStatusQueuedSchema = z.object({
  status: z.literal('queued'),
  queue: QueueInfoSchema,
  ticket_id: UuidSchema.optional(),
  session_id: UuidSchema.optional().nullable(),
  reminder: QueueReminderStateSchema.optional(),
});

export const QueueMessageSchema = z.union([
  QueueAuthSchema,
  QueueHeartbeatSchema,
  QueueUpdateSchema,
  QueueAssignedSchema,
  QueueOfferedSchema,
  QueueStatusAssignedSchema,
  QueueStatusOfferedSchema,
  QueueStatusQueuedSchema,
]);

export const QueueOutboundMessageSchema = z.union([
  z.object({ type: z.literal('auth'), token: z.string(), trace_id: UuidSchema }),
  z.object({ type: z.literal('subscribe'), ticket_id: UuidSchema }),
  z.object({ type: z.literal('heartbeat') }),
]);

export type QueueMessage = z.infer<typeof QueueMessageSchema>;
export type QueueOutboundMessage = z.infer<typeof QueueOutboundMessageSchema>;
export type QueueAssignedPayload = z.infer<typeof QueueAssignedSchema>;
export type QueueOfferedPayload = z.infer<typeof QueueOfferedSchema>;

export const parseQueueMessage = (raw: unknown): Result<QueueMessage, ContractError> => {
  const parsed = QueueMessageSchema.safeParse(raw);
  if (!parsed.success) {
    return {
      ok: false,
      err: toContractError(parsed.error, { source: 'queue-ws' }),
    };
  }
  return { ok: true, value: parsed.data };
};
